import { useMemo } from 'react'

import { updateSkus } from '@assets/fwnInit/fwnShopping/updateSkus'
import type { Channel } from '@core/channel'
import type { FeedSourceConfig } from '@core/feed'
import { FeedType } from '@core/feed'
import { filterTruthy } from '@core/helpers/filterTruthy'
import uniq from '@core/lodash/uniq'
import type { IAnyWidgetParams } from '@core/types'
import { AppContextType } from '@embed/components/helpers'

export interface Props {
  appContextType: AppContextType
  params: IAnyWidgetParams
  channel?: Channel | null
}

type Priority =
  'videos' | 'skus' | 'playlist' | 'hashtag' | 'playlist_collection' | 'channel'

/**
 * Hook to map params to embed feed configuration which is
 * used to configure API calls
 */
export const useMapParamsToConfiguration = ({
  appContextType,
  params,
  channel,
}: Props): FeedSourceConfig[] | null => {
  const hasCohorts = useMemo(
    () => Object.keys(params).some((param) => param.startsWith('cohort_')),
    [params],
  )

  const username = params.channel || channel?.username

  const skus = updateSkus(params.skus)

  return useMemo(() => {
    const defaultValues = {
      firstVideoId: params.video || params.video_id,
      initialVideoId: params.player_scroll_to_video_id,
      maxVideos:
        // Ignore the max videos for carousel in grid mode
        appContextType === AppContextType.EMBED_GRID_CONTEXT_TYPE
          ? undefined
          : params.max_videos,
    }
    if (params.channel || params.channel_id) {
      if (params.channel_id && !username) {
        return null
      }
      if (!(
        params.videos ||
        params.hashtag ||
        params.playlist ||
        params.playlist_collection ||
        params.skus ||
        hasCohorts
      )) {
        return [
          {
            ...defaultValues,
            type: FeedType.USER_TIMELINE,
            username,
          },
        ]
      }
      const configs =
        params.priority?.split(',').map((p) => {
          switch (p.trim() as Priority) {
            case 'videos':
              return params.videos
                ? {
                    ...defaultValues,
                    type: FeedType.USER_VIDEOS as const,
                    username,
                    videoIds: uniq(
                      params.videos.split(',').map((v) => v.trim()),
                    ),
                  }
                : null
            case 'hashtag':
              return params.hashtag
                ? {
                    ...defaultValues,
                    type: FeedType.USER_TIMELINE_HASHTAG as const,
                    username,
                    hashtagExpression: params.hashtag,
                  }
                : null
            case 'playlist':
              return params.playlist
                ? {
                    ...defaultValues,
                    type: FeedType.PLAYLIST as const,
                    username,
                    playlistId: params.playlist,
                  }
                : null
            case 'playlist_collection':
              return params.playlist_collection
                ? {
                    ...defaultValues,
                    type: FeedType.PLAYLIST_COLLECTION as const,
                    username,
                  }
                : null
            case 'skus':
              return skus
                ? {
                    ...defaultValues,
                    type: FeedType.PRODUCT_SKU as const,
                    username,
                    skus: skus,
                  }
                : null
            case 'channel':
              return {
                ...defaultValues,
                type: FeedType.USER_TIMELINE as const,
                username,
              }

            default:
              return null
          }
        }) ?? []

      return filterTruthy(configs)
    }
    return null
  }, [
    appContextType,
    hasCohorts,
    params.channel_id,
    params.channel,
    params.hashtag,
    params.max_videos,
    params.player_scroll_to_video_id,
    params.playlist_collection,
    params.playlist,
    params.priority,
    params.skus,
    params.video_id,
    params.video,
    params.videos,
    username,
    skus,
  ])
}
